function _toConsumableArray(arr) {
  if (Array.isArray(arr)) {
    for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) {
      arr2[i] = arr[i];
    }
    return arr2;
  } else {
    return Array.from(arr);
  }
}
function _objectWithoutProperties(obj, keys) {
  var target = {};
  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }
  return target;
}
import React from 'react';
import APIcon from './APIcon';
import Spinner from './Spinner/Spinner';
import PropTypes from 'prop-types';
import PaginationContainer from '../common/Pagination/PaginationContainer';
import { getBEMClasses } from '../../helpers/cssClassesHelper';
import '../../styles/layout/Table.css';
var filterInvalid = function filterInvalid() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var selectedInstrument = _ref.selectedInstrument,
    rowIndex = _ref.rowIndex,
    rest = _objectWithoutProperties(_ref, ['selectedInstrument', 'rowIndex']);
  return rest;
};
var APTable = function APTable(props, context) {
  var title = props.title,
    columns = props.columns,
    baseClass = props.baseClass,
    rows = props.rows,
    empty = props.empty,
    onRowClicked = props.onRowClicked,
    fetching = props.fetching,
    headerOutside = props.headerOutside,
    pageSize = props.pageSize,
    minRow = props.minRow,
    alwaysShowPagination = props.alwaysShowPagination,
    rowProps = props.rowProps,
    selectedRow = props.selectedRow,
    componentName = props.componentName,
    usePagination = props.usePagination,
    showOnlyActivePage = props.showOnlyActivePage;
  var flexTable = getBEMClasses('flex-table');
  var customClass = baseClass || function () {
    return '';
  };
  var header = columns.map(function (col, index) {
    return React.createElement('div', {
      'data-test': col.dataTest,
      key: index,
      title: col.title || undefined,
      className: flexTable('column') + ' ' + customClass('header-column', col.classModifier)
    }, React.createElement('div', {
      className: flexTable('header-cell', headerOutside ? 'absolute' : 'fixed') + ' ' + customClass('fixed', col.classModifier)
    }, col.header));
  });
  var emptyRow = function emptyRow() {
    return React.createElement('div', {
      className: flexTable('row', 'empty') + ' ' + customClass('row-empty')
    }, React.createElement('div', {
      className: flexTable('column', 'empty') + ' ' + customClass('column-empty')
    }, React.createElement(APIcon, {
      name: 'stop',
      customClass: flexTable('empty-icon') + ' ' + customClass('empty-icon')
    }), empty));
  };
  var getTableRows = function getTableRows(data) {
    return data && data.length && data[0] === undefined ? data.map(function (row, rowIndex) {
      return React.createElement('div', {
        key: rowIndex,
        className: flexTable('row', 'no-hover') + ' ' + customClass('row')
      }, rowIndex === 0 && emptyRow());
    }) : data.map(function (row, rowIndex) {
      return React.createElement('div', Object.assign({}, row && filterInvalid(rowProps(row, rowIndex)), {
        onClick: function onClick() {
          return row && onRowClicked && onRowClicked(row);
        },
        key: rowIndex,
        className: flexTable('row', !row && 'no-hover') + ' ' + customClass('row', selectedRow.key && selectedRow.value === row[selectedRow.key] ? 'selected' : '')
      }), row && columns.map(function (col, colIndex) {
        return React.createElement('div', {
          style: col.style,
          key: col.accessor || colIndex,
          className: flexTable('column') + ' ' + customClass('column', col.classModifier)
        }, col.cell ? col.cell(row, rowProps(row, rowIndex)) : row[col.accessor]);
      }));
    });
  };
  var renderTable = function renderTable(data, containerClasses) {
    return React.createElement('div', {
      className: flexTable('wide-row-container')
    }, React.createElement('div', {
      className: flexTable() + ' ' + customClass() + ' ' + containerClasses
    }, title && React.createElement('div', {
      className: flexTable('title') + ' ' + customClass('table-title')
    }, title), React.createElement('div', {
      className: flexTable('header', 'background') + ' ' + customClass('table-header', 'background')
    }), headerOutside && React.createElement('div', {
      className: flexTable('header') + ' ' + customClass('table-header', baseClass.classModifier)
    }, header), React.createElement('div', {
      className: flexTable('body') + ' ' + customClass('body')
    }, !headerOutside && React.createElement('div', {
      className: flexTable('header', 'inside') + ' ' + customClass('table-header', baseClass.classModifier)
    }, header), fetching ? React.createElement(Spinner, {
      text: context.t('Loading...'),
      customClass: baseClass
    }) : data.length === 0 ? emptyRow() : getTableRows(data))));
  };
  var getMinimumOf = function getMinimumOf(data, min) {
    var emptyRows = min - data.length;
    return minRow && min && emptyRows > 0 ? [].concat(_toConsumableArray(data), _toConsumableArray(new Array(Math.min(minRow || emptyRows, emptyRows)))) : data;
  };
  var renderTableWithPagination = function renderTableWithPagination(totalPages, data) {
    var containerClasses = customClass('body', 'multipage');
    return React.createElement(PaginationContainer, {
      totalPages: totalPages,
      containerClassName: flexTable('pagination-container'),
      content: function content(page) {
        var rows = data.slice(pageSize * page, pageSize * (page + 1));
        return renderTable(getMinimumOf(rows, minRow || pageSize), containerClasses);
      },
      componentName: componentName,
      customClass: baseClass(),
      showOnlyActivePage: showOnlyActivePage
    });
  };
  var totalPages = Math.ceil(rows.length / pageSize || rows.length);
  var containerClasses = customClass('body', 'single-page');
  return usePagination && (alwaysShowPagination || pageSize && totalPages > 1) ? renderTableWithPagination(totalPages, rows) : renderTable(getMinimumOf(rows, minRow || pageSize), containerClasses);
};
APTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    accessor: PropTypes.string,
    header: PropTypes.string,
    cell: PropTypes.func,
    width: PropTypes.string,
    classModifier: PropTypes.string
  })).isRequired,
  rows: PropTypes.array.isRequired,
  rowProps: PropTypes.func,
  empty: PropTypes.string,
  onRowClicked: PropTypes.func,
  baseClass: PropTypes.func,
  fetching: PropTypes.bool,
  minRow: PropTypes.number,
  pageSize: PropTypes.number,
  alwaysShowPagination: PropTypes.bool,
  selectedRow: PropTypes.object,
  usePagination: PropTypes.bool
};
APTable.defaultProps = {
  headerOutside: true,
  rowProps: function rowProps(row, rowIndex) {
    return {
      rowIndex: rowIndex
    };
  },
  selectedRow: {
    key: '',
    value: ''
  },
  usePagination: true
};
APTable.contextTypes = {
  t: PropTypes.func.isRequired
};
export default APTable;